/* ============================================================
   SINGSPACE ADMIN - PAGE WHATSAPP IA (Sobre & Complet)
   ============================================================ */

import { Activity, Bot, MessageCircle, MessageSquare, RefreshCw } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { KPIWidget } from '../components/shared/KPIWidget';
import { Button } from '../components/ui/button';
import { BotConfigSection } from '../components/whatsapp/BotConfigSection';
import { MessagesView } from '../components/whatsapp/MessagesView';
import { SessionControl } from '../components/whatsapp/SessionControl';
import { StatusPill } from '../components/whatsapp/StatusPill';
import { MOCK_MESSAGES } from '../constants/whatsapp.constants';
import { ContextDoc, Message, SessionStatus } from '../types/whatsapp.types';

export function WhatsAppPage() {
  const [sessionStatus, setSessionStatus] = useState<SessionStatus>('disconnected');
  const [messages, setMessages] = useState<Message[]>(MOCK_MESSAGES);
  const [contextDocs, setContextDocs] = useState<ContextDoc[]>([]);
  const [botPrompt, setBotPrompt] = useState<string>(
    `Tu es l'assistant virtuel de SING SA, une société d'incubation numérique basée à Libreville, Gabon. Tu gères les réservations d'espaces de coworking, de salles de conférence et de bureaux permanents.\n\nRègles:\n- Réponds toujours en français\n- Sois professionnel et courtois\n- Fournis les tarifs quand on te les demande\n- Oriente vers le site web pour les réservations complexes\n- Ne divulgue jamais d'informations confidentielles`,
  );
  const [isPromptSaved, setIsPromptSaved] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const fileInputRef = useRef<HTMLInputElement>(null!);
  const msgEndRef = useRef<HTMLDivElement>(null!);

  useEffect(() => {
    msgEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // --- Handlers ---
  const handleStartSession = () => {
    setSessionStatus('scanning');
  };

  const handleSimulateConnect = () => {
    setSessionStatus('connected');
  };

  const handleDisconnect = () => {
    if (confirm('Voulez-vous vraiment déconnecter la session WhatsApp ?')) {
      setSessionStatus('disconnected');
      setMessages([]);
    }
  };

  const handleRefresh = async () => {
    setIsRefreshing(true);
    await new Promise((resolve) => setTimeout(resolve, 800));
    setIsRefreshing(false);
  };

  const handleUploadContext = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) return;
    const newDocs: ContextDoc[] = [];
    for (let i = 0; i < files.length; i++) {
      const f = files[i];
      if (f) {
        newDocs.push({
          id: `doc-${Date.now()}-${i}`,
          name: f.name,
          size: f.size,
          uploadedAt: new Date(),
        });
      }
    }
    setContextDocs((prev) => [...prev, ...newDocs]);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleRemoveDoc = (id: string) => {
    setContextDocs((prev) => prev.filter((d) => d.id !== id));
  };

  const handleSavePrompt = () => {
    setIsPromptSaved(true);
  };

  return (
    <div className="space-y-6 pb-12">
      {/* --- HEADER --- */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <MessageCircle size={24} className="text-[#00303c]" />
          <div>
            <h1 className="text-2xl font-semibold text-slate-900">WhatsApp IA</h1>
            <p className="text-slate-500 text-sm mt-0.5">
              Gestion de l'assistant virtuel et monitoring des interactions
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <StatusPill status={sessionStatus} />
          <Button type="button" variant="outline" size="sm" onClick={handleRefresh} disabled={isRefreshing}>
            <RefreshCw size={16} className={`mr-2 ${isRefreshing ? 'animate-spin' : ''}`} />
            Actualiser
          </Button>
        </div>
      </div>

      {/* --- KPIs --- */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        <KPIWidget
          title="Messages Reçus"
          value={String(messages.length)}
          subtext="Dernières 24h"
          trend="up"
          color="primary"
          icon={<MessageSquare />}
        />
        <KPIWidget
          title="Réponses Bot"
          value={String(messages.filter((m) => m.botReplied).length)}
          subtext={`Taux: ${messages.length > 0 ? Math.round((messages.filter((m) => m.botReplied).length / messages.length) * 100) : 0}%`}
          trend="up"
          color="success"
          icon={<Bot />}
        />
        <KPIWidget
          title="Temps Moyen"
          value="1.2s"
          subtext="Vitesse de réponse"
          trend="down"
          color="secondary"
          icon={<Activity />}
        />
      </div>

      {/* --- GRID LAYOUT --- */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* COLONNE GAUCHE: Session Control */}
        <div className="lg:col-span-1">
          <SessionControl
            sessionStatus={sessionStatus}
            handleStartSession={handleStartSession}
            handleSimulateConnect={handleSimulateConnect}
            handleDisconnect={handleDisconnect}
          />
        </div>

        {/* COLONNE DROITE: Messages View */}
        <div className="lg:col-span-2">
          <MessagesView messages={messages} sessionStatus={sessionStatus} msgEndRef={msgEndRef} />
        </div>
      </div>

      {/* --- CONFIGURATION BOT (FULL WIDTH) --- */}
      <BotConfigSection
        botPrompt={botPrompt}
        setBotPrompt={setBotPrompt}
        isPromptSaved={isPromptSaved}
        setIsPromptSaved={setIsPromptSaved}
        handleSavePrompt={handleSavePrompt}
        fileInputRef={fileInputRef}
        handleUploadContext={handleUploadContext}
        contextDocs={contextDocs}
        handleRemoveDoc={handleRemoveDoc}
      />
    </div>
  );
}
